import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  firstValueFrom,
  Subscription
} from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { DelegatedTeam } from 'src/app/auth/models/UserData';
import { AuthService } from 'src/app/auth/services/auth.service';
import { DialogService } from 'src/app/shared/dialogs/services/DialogService';
import { LabelContext } from 'src/app/shared/framework/labels/models/LabelContext';
import { LabelService } from 'src/app/shared/framework/labels/services/label.service';
import {
  LearningPathActivityStudentDetailed,
  LearningPathStudentGeneral,
  LearnpathActivity,
  LearnpathActivityDetailed
} from '../../models/interfaces';
import { LearnpathService } from '../../services/learnpath.service';
import { LearnpathEmployeeDetailComponent } from '../learnpath-employee-detail/learnpath-employee-detail.component';

@Component({
  selector: 'app-learning-path-employee-overview',
  templateUrl: './learning-path-employee-overview.component.html',
  styleUrls: ['./learning-path-employee-overview.component.scss'],
})
export class LearningPathEmployeeOverviewComponent implements OnInit, OnDestroy {
  private _paramsSubscription$: Subscription;
  private _employeeSubscription: Subscription;
  private subscriptions: Subscription[] = [];

  private _learningPathId: number;
  public get learningPathId(): number {
    return this._learningPathId;
  }
  public set learningPathId(value: number) {
    this._learningPathId = value;
    this.fetchData();
  }

  public employees: LearningPathStudentGeneral[];
  public activitiesDetails: LearnpathActivityDetailed[] = [];

  public activities: LearnpathActivity[] = [];
  public students: StudentActivityList[] = [];

  public loading = true;
  public allCollapsed = false;

  public employeeSearch: string;
  public statusSearch: number = 0;
  public studentsFiltered: StudentActivityList[] = [];
  public sort = { column: 3, dir: true };

  public canSendReminders: boolean;

  public today: Date = new Date();

  private _inputLearnpathId?: number;
  get inputLearnpathId(): number {
    return this._inputLearnpathId;
  }
  private _labelContext: LabelContext;
  private _labelContextLMS: LabelContext;

  @Input() set inputLearnpathId(id: number) {
    if (id && id > 0) {
      this.learningPathId = +id;
      this._inputLearnpathId = +id;
    }
  }

  @Input() deadline: boolean;

  public constructor(
    private _route: ActivatedRoute,
    private _learnPathService: LearnpathService,
    private modalService: NgbModal,
    private _router: Router,
    public _toastrService: ToastrService,
    private labelService: LabelService,
    private authService: AuthService,
    private dialogService: DialogService
  ) { }

  public async ngOnInit() {
    this._labelContext = await firstValueFrom(this.labelService.getContext('general'));
    this._labelContextLMS = await firstValueFrom(this.labelService.getContext('learningpath'));

    this.subscriptions.push(
      this._learnPathService.canSendReminderMails().subscribe(e => {
        this.canSendReminders = e;
      })
    );

    if (this.inputLearnpathId) {
      this.learningPathId = +this.inputLearnpathId;
    } else {
      this._paramsSubscription$ = this._route.parent.params.subscribe(
        (params: Params) => {
          this.learningPathId = +params.id;
        }
      );
    }
  }

  public collapseAll() {
    this.allCollapsed = !this.allCollapsed;

    for (const participant of this.students) {
      participant.collapsed = this.allCollapsed;
    }
  }

  public getStatusName(status: number) {
    switch (status) {
      case 2: return 'not_started';
      case 3: return 'started';
      case 4: return 'finished';
      case 6: return 'unsubscribed';
    }
    return 'not_started';
  }

  public getStatusClass(status: number) {
    switch (status) {
      case 2: return '#bfbfbf';
      case 3: return '#ee7622';
      case 4: return '#28a745';
      case 6: return 'red';
    }
    return 'not_started';
  }

  public getActivityStatusName(status: number) {
    switch (status) {
      case 1: return 'not_started';
      case 2: return 'started';
      case 3: return 'finished';
      case 4: return 'unsubscribed';
    }
    return 'not_started';
  }

  public getActivityStatusClass(status: number) {
    switch (status) {
      case 1: return '#bfbfbf';
      case 2: return '#ee7622';
      case 3: return '#28a745';
    }
    return 'not_started';
  }

  @HostListener('window:resize')
  resizeFrame() {
    setTimeout(() => {
      // var iframes = window.parent.document.getElementsByTagName('iframe');
      // for (let index = 0; index < iframes.length; index++) {
      //   const element = iframes[index];
      //   element.style.height = (element.contentWindow.document.body.scrollHeight) + 'px';
      // }
      try {
        (<any>window.top).initIframeResize();
        // window.document.body.style.height = "100%";
      } catch (e) { }
    }, 500);
  }

  public iterator() {
    let filtered = [];

    let search = (this.employeeSearch || '').trim().toLowerCase();

    if (search && search.length > 0) {
      filtered = this.students.filter(e => (e.name).toLowerCase().indexOf(search) > -1);
    } else {
      filtered = this.students;
    }

    filtered = filtered.filter(e => e.learningPathStudentStatus == this.statusSearch || (this.statusSearch == 0 && e.learningPathStudentStatus != 6));

    filtered = filtered.sort((a, b) => {
      const isAsc = !this.sort.dir;
      switch (this.sort.column) {
        case 1:
          return compare(a.lastName + a.firstName, b.lastName + b.firstName, isAsc);
        case 2:
          return compare(a.learningPathStudentStatus, b.learningPathStudentStatus, isAsc);
        case 3:
          return compareDates(a.inviteDate, b.inviteDate, isAsc);
        case 4:
          return compareDates(a.finishDate, b.finishDate, isAsc);

        default:
          return 0;
      }
    })

    this.studentsFiltered = [...filtered];
    this.resizeFrame();
  }

  public changeSort(column: number) {
    if (this.sort.column == column) {
      this.sort.dir = !this.sort.dir;
    } else {
      this.sort.column = column;
      this.sort.dir = false;
    }
    this.iterator();
  }

  public sendReminder(learningPathStudentId: number) {
    this.subscriptions.push(
      this._learnPathService
        .sendReminder(learningPathStudentId)
        .pipe(
          take(1),
          tap(() => {
            this.subscriptions.push(
              this.labelService
                .getLabel('sentsuccessfully', 'general')
                .subscribe((lbl) => {
                  (<any>window.top).showToastr(true, lbl.labelText);
                })
            );
          })
        )
        .subscribe()
    );
  }

  public resumeLearningPath(learningPathStudentId: number): void {
    let message = this._labelContextLMS.get('are_you_sure_resume_message').labelText;
    let title = this._labelContextLMS.get('are_you_sure_resume').labelText;
    this.dialogService.confirm({ message: message, title: title, centered: false })
      .then(() => {
        this.subscriptions.push(
          this._learnPathService.resumeEmployeeLearningpath(learningPathStudentId).subscribe(() => {
            this.fetchData();
            this.subscriptions.push(
              this.labelService
                .getLabel('successfully_resumed', 'learningpath')
                .subscribe((lbl) => {
                  (<any>window.top).showToastr(true, lbl.labelText);
                })
            );
          })
        );
      }, () => { })
  }

  public restartLearningPath(learningPathStudentId: number): void {
    let message = this._labelContextLMS.get('are_you_sure_restart_message').labelText;
    let title = this._labelContextLMS.get('are_you_sure_restart').labelText;
    this.dialogService.confirm({ message: message, title: title, centered: false })
      .then(() => {
        this.subscriptions.push(
          this._learnPathService.restartEmployeeLearningpath(learningPathStudentId).subscribe(() => {
            this.fetchData();
            this.subscriptions.push(
              this.labelService
                .getLabel('successfully_restarted', 'learningpath')
                .subscribe((lbl) => {
                  (<any>window.top).showToastr(true, lbl.labelText);
                })
            );
          })
        );
      }, () => { })
  }

  public updateStatusLearningPath(learningPathStudentId: number): void {
    let message = this._labelContext.get('action_cannot_be_undone').labelText;
    let title = this._labelContextLMS.get('are_you_sure_complete').labelText;
    this.dialogService.confirm({ message: message, title: title, centered: false })
      .then(() => {
        this.subscriptions.push(
          this._learnPathService.completeEmployeeLearningpath(learningPathStudentId).subscribe(() => {
            this.fetchData();
            this.subscriptions.push(
              this.labelService
                .getLabel('successfully_completed', 'learningpath')
                .subscribe((lbl) => {
                  (<any>window.top).showToastr(true, lbl.labelText);
                })
            );
          })
        );
      }, () => { })
  }

  public unsubscribe(studentId: number): void {
    let message = this._labelContext.get('action_cannot_be_undone').labelText;
    let title = this._labelContextLMS.get('are_you_sure_unsubscribe').labelText;
    this.dialogService.confirm({ message: message, title: title, centered: false })
      .then(() => {
        this.subscriptions.push(
          this._learnPathService.unsubscribeEmployee(studentId).subscribe(() => {
            this.fetchData();
            this.subscriptions.push(
              this.labelService
                .getLabel('successfully_unsubscribed', 'learningpath')
                .subscribe((lbl) => {
                  (<any>window.top).showToastr(true, lbl.labelText);
                })
            );
          })
        );
      }, () => { })
  }

  public updateActivity(activity: LearningPathActivityStudentDetailed) {
    activity.editMode = true;
    activity.newActivityStatusId = activity.activityStatusId;
    activity.newManagerComment = activity.managerComment;
  }

  public saveActivity(activity: LearningPathActivityStudentDetailed) {
    if (activity.editMode) {
      activity.editMode = false;
      activity.activityStatusId = activity.newActivityStatusId;
      activity.managerComment = activity.newManagerComment;

      this.subscriptions.push(
        this._learnPathService.saveActivityAdmin(activity.studentActivityId, activity.managerComment, activity.activityStatusId).subscribe(() => {
          let label = this._labelContext.get('savedsuccessfully').labelText;
          this._toastrService.clear();
          this._toastrService.success(label, undefined, { timeOut: 1000 });
          this.fetchData()
        })
      );
    }
  }

  public cancelActivity(activity: LearningPathActivityStudentDetailed) {
    if (activity.editMode) {
      activity.editMode = false;
      activity.newManagerComment = '';
      activity.newActivityStatusId = activity.activityStatusId;
    }
  }

  public open(url) {
    window.open(url, '_blank');
  }

  public setDefaultPic(empl: StudentActivityList) {
    empl.pictureError = true;
  }

  public navigateToCurrentCoursesProfile(employee: StudentActivityList) {
    let parameters = "";
    if (localStorage.getItem('delegate_view') === '1') {
      let activeDelegation: DelegatedTeam =
        this.authService.getActiveDelegation();

      let delegatedMgrId = activeDelegation?.studentId ?? null;
      if (delegatedMgrId) {
        parameters += "&delegate=1&studentid=" + delegatedMgrId;
      }
    }
    let url = '/perfNav/student/ML/common/schedule/viewscheduledetailgeneral.asp?item=' + employee.studentId + parameters;
    (<any>window.top).openOverlay(url);
  }

  public downloadAttachment(attachment: any): void {
    const blob: Blob = this.convertToBlob(attachment.fileData, attachment.contentType);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.setAttribute('style', 'display: none');
    a.href = url;
    a.download = attachment.fileName;
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  private convertToBlob(b64Data: string, contentType: string) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  };


  public ngOnDestroy(): void {
    if (this._paramsSubscription$) {
      this._paramsSubscription$.unsubscribe();
    }
    if (this._employeeSubscription) {
      this._employeeSubscription.unsubscribe();
    }

    for (const subscr of this.subscriptions) {
      subscr.unsubscribe();
    }
  }

  public fetchData(): void {
    this.loading = true;
    this.employees = [];
    this.activitiesDetails = [];
    this.students = [];

    this.fetchEmployeesAndActivities();
  }

  private fetchEmployeesAndActivities(): void {
    let currentDate = new Date();
    this.subscriptions.push(
      this._learnPathService
        .getSubscribedEmployees(this.learningPathId, null, true)
        .subscribe((e) => {
          this.employees = [...e];

          this.subscriptions.push(
            this._learnPathService
              .getActivitiesDetailed(this.learningPathId)
              .subscribe((activitiesDetailed) => {

                this.activitiesDetails = [...activitiesDetailed.map((activityDetailed) => {
                  activityDetailed.learningPathStudentActivities =
                    activityDetailed.learningPathStudentActivities.map((la) => {
                      la.order = activityDetailed.order;
                      la.name = activityDetailed.name;
                      return la;
                    });
                  return activityDetailed;
                })];

                this.students = [...this.employees
                  .map((student: LearningPathStudentGeneral) => {
                    const studentActivities = [...this.activitiesDetails
                      .reduce(
                        (
                          prev: LearningPathActivityStudentDetailed[],
                          next,
                          i
                        ) => prev.concat(next.learningPathStudentActivities),
                        []
                      )
                      .filter(
                        (e) =>
                          e.learningPathStudentId ==
                          student.learningPathStudentId
                      )
                      .sort(this.studentActivitySort)
                    ];

                    return {
                      firstName: student.firstName,
                      lastName: student.lastName,
                      name: student.firstName + ' ' + student.lastName,
                      hasPhoto: studentActivities[0]?.hasPhoto,
                      studentId: studentActivities[0]?.studentId,
                      learningPathStudentId: student.learningPathStudentId,
                      learningPathStudentStatus: student.learningPathStudentStatus,
                      inviteDate: student.inviteDate,
                      finishDate: student.finishedDate,
                      collapsed: student.collapsed,
                      activities: studentActivities,
                      deadline: student.deadline,
                      deadlineClose: student.deadlineClose,
                      editMode: false,
                    };
                  })];

                if (this.studentsFiltered && this.studentsFiltered.length) {
                  for (const student of this.students) {
                    let foundStudent = this.studentsFiltered.filter((i) => {
                      return i.learningPathStudentId == student.learningPathStudentId;
                    })[0];
                    if (foundStudent) {
                      student.collapsed = foundStudent.collapsed;
                    }
                  }
                }
                this.iterator();
                this.loading = false;
              })
          );
        })
    );
  }

  private studentActivitySort(
    firstActivity: LearningPathActivityStudentDetailed,
    secondActivity: LearningPathActivityStudentDetailed
  ): number {
    if (firstActivity.order < secondActivity.order) {
      return -1;
    }
    if (firstActivity.order > secondActivity.order) {
      return 1;
    }
    return 0;
  }

  public getActivityClass(
    activity: LearningPathActivityStudentDetailed
  ): string {
    switch (activity.activityStatusId) {
      case 1:
        return 'notStarted';
      case 2:
        return 'started';
      case 3:
        return 'completed';
    }
  }

  public getDaysSinceDeadline(
    deadline: Date
  ): number {
    if (!deadline) { return 0; }

    deadline = new Date(deadline);
    let today = new Date();
    let days = Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 3600 * 24));

    if (days < 0) {
      days = 0;
    }
    return days;
  }

  public calculateProgressPercentage(
    deadline: Date
  ): number {
    let daysUntilDeadline = this.getDaysSinceDeadline(deadline);

    if (daysUntilDeadline > 100) {
      return 0;
    }

    return 100 - daysUntilDeadline;
  }


  public calculateProgressPercentageType(
    deadline: Date
  ): string {
    let days = this.getDaysSinceDeadline(deadline);
    if (days >= 60) {
      return "info";
    } else if (days >= 30) {
      return "warning"
    } else {
      return "danger"
    }
  }

  public openEmployeeDetail(
    learningPathId: number,
    learningPathStudentId: number
  ) {
    let modal = this.modalService.open(LearnpathEmployeeDetailComponent, {
      size: 'lg',
    });
    modal.componentInstance.learningPathId = learningPathId;
    modal.componentInstance.learningPathStudentId = learningPathStudentId;

    modal.result.then(
      () => {
        this.fetchData();
      },
      () => {
        this.fetchData();
      }
    );
  }
}

export interface StudentActivityList {
  firstName: string;
  lastName: string;
  name?: string;
  hasPhoto: boolean;
  pictureError?: boolean;
  studentId: number;
  learningPathStudentId: number;
  learningPathStudentStatus: number;
  inviteDate: Date;
  finishDate?: Date;
  collapsed?: boolean;
  activities: LearningPathActivityStudentDetailed[];
  deadline?: Date;
  deadlineClose?: boolean;
}

function compare(a: number | string, b: number | string, isAsc: boolean) {
  if (a && typeof a == 'string') {
    a = a.toLowerCase();
  }
  if (b && typeof b == 'string') {
    b = b.toLowerCase();
  }

  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}

function compareDates(a: Date, b: Date, isAsc: boolean) {
  return (
    (new Date(a).getTime() < new Date(b).getTime() ? -1 : 1) * (isAsc ? 1 : -1)
  );
}
